import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";
import {
  MEMBERSHIP_SOURCE_FILTER_OPTIONS,
  MEMBERSHIP_TIER_FILTER_OPTIONS,
  PHONE_VERIFICATION_FILTER_OPTIONS,
  QUICK_FILTERS,
  STATUS_FILTER_OPTIONS,
  VERIFICATION_STATE_FILTER_OPTIONS,
} from "./userManagementFilters.js";

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
  },
  summary: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
    },
  },
  sectionLabel: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  quickFilterChip: {
    borderRadius: 16,
    borderColor: theme.palette.divider,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
  },
  quickFilterChipActive: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
}));

const ALL_SENTINEL_FILTER_FIELDS = new Set([
  "status",
  "membershipTier",
  "verificationState",
  "phoneVerificationState",
  "membershipSource",
]);

function isActiveFilterValue(field, value) {
  if (typeof value === "string") {
    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return false;
    }

    if (
      ALL_SENTINEL_FILTER_FIELDS.has(field) &&
      normalizedValue === "all"
    ) {
      return false;
    }

    return true;
  }

  return Boolean(value);
}

function hasActiveFilters(filters) {
  return Object.keys(filters || {}).some((key) =>
    isActiveFilterValue(key, filters[key])
  );
}

function renderSelectOptions(options) {
  return options.map((option) => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  ));
}

export default function UserManagementFilterBar({
  filters,
  onChange,
  onReset,
  totalCount,
  filteredCount,
}) {
  const classes = useStyles();
  const filtersActive = hasActiveFilters(filters);

  const handleFieldChange = (field) => (event) => {
    onChange({ [field]: event.target.value });
  };

  return (
    <Paper variant="outlined" className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="subtitle1" className={classes.title}>
            Find users faster
          </Typography>
          <Typography variant="body2" className={classes.summary}>
            Showing {filteredCount} of {totalCount} users
          </Typography>
        </div>
        <Button
          color="primary"
          size="small"
          onClick={onReset}
          disabled={!filtersActive}
        >
          Clear filters
        </Button>
      </div>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            className={classes.searchField}
            fullWidth
            variant="outlined"
            size="small"
            value={filters.search}
            onChange={handleFieldChange("search")}
            placeholder="Search by name, email, nickname, or Steam ID"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" className={classes.sectionLabel}>
            Quick filters
          </Typography>
          <div className={classes.chipRow}>
            {QUICK_FILTERS.map((filter) => {
              const isActive = filters.quickFilter === filter.id;

              return (
                <Chip
                  key={filter.id}
                  clickable
                  label={filter.label}
                  size="small"
                  variant={isActive ? "default" : "outlined"}
                  className={`${classes.quickFilterChip} ${
                    isActive ? classes.quickFilterChipActive : ""
                  }`}
                  onClick={() =>
                    onChange({
                      quickFilter: isActive ? "" : filter.id,
                    })
                  }
                />
              );
            })}
          </div>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <TextField
            fullWidth
            select
            label="Status"
            size="small"
            variant="outlined"
            value={filters.status}
            onChange={handleFieldChange("status")}
            SelectProps={{ native: true }}
          >
            {renderSelectOptions(STATUS_FILTER_OPTIONS)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <TextField
            fullWidth
            select
            label="Membership"
            size="small"
            variant="outlined"
            value={filters.membershipTier}
            onChange={handleFieldChange("membershipTier")}
            SelectProps={{ native: true }}
          >
            {renderSelectOptions(MEMBERSHIP_TIER_FILTER_OPTIONS)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            fullWidth
            select
            label="Verification"
            size="small"
            variant="outlined"
            value={filters.verificationState}
            onChange={handleFieldChange("verificationState")}
            SelectProps={{ native: true }}
          >
            {renderSelectOptions(VERIFICATION_STATE_FILTER_OPTIONS)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <TextField
            fullWidth
            select
            label="Phone verification"
            size="small"
            variant="outlined"
            value={filters.phoneVerificationState}
            onChange={handleFieldChange("phoneVerificationState")}
            SelectProps={{ native: true }}
          >
            {renderSelectOptions(PHONE_VERIFICATION_FILTER_OPTIONS)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            fullWidth
            select
            label="Membership source"
            size="small"
            variant="outlined"
            value={filters.membershipSource}
            onChange={handleFieldChange("membershipSource")}
            SelectProps={{ native: true }}
          >
            {renderSelectOptions(MEMBERSHIP_SOURCE_FILTER_OPTIONS)}
          </TextField>
        </Grid>
      </Grid>
    </Paper>
  );
}

UserManagementFilterBar.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    quickFilter: PropTypes.string,
    status: PropTypes.string,
    membershipTier: PropTypes.string,
    verificationState: PropTypes.string,
    phoneVerificationState: PropTypes.string,
    membershipSource: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  totalCount: PropTypes.number.isRequired,
  filteredCount: PropTypes.number.isRequired,
};
